import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Modal, Button, Spin } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

const WorkerApplication = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  const [formFields, setFormFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formState, setFormState] = useState({});
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);
  const isPaymentExecuted = useRef(false); // To track if payment has been executed
  const [formMeta, setFormMeta] = useState({}); // 👈 add this to store full form including payment_amount

  // Reset payment states on component mount
  useEffect(() => {
    setIsPaymentSuccess(false);
    setIsPaymentFailed(false);
  }, []);

  // Fetch form fields from the API
  useEffect(() => {
    const fetchFormFields = async () => {
      try {
        const response = await axios.get(`${apiUrl}/forms/2`);
        setFormFields(response.data.fields || []);
        setFormMeta(response.data); 
      } catch (error) {
        console.error("Error fetching form fields:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormFields();
  }, [apiUrl]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    try {
      // Step 1: Create bKash payment request
      const paymentResponse = await axios.post(`${apiUrl}/create_payment/`, {
        amount: formMeta.payment_amount, // 👈 dynamically use the amount
        callback_url: `${window.location.origin}/worker-registration`, // Callback URL
      });
  
      if (paymentResponse.data.status === "success") {
        const paymentID = paymentResponse.data.payment_id; // Get payment_id from create_payment
  
        // Step 2: Send form data to backend for temporary storage
        await axios.post(`${apiUrl}/temp_form_data/`, {
          payment_id: paymentID,
          form_data: formState,
        }, {
          headers: {
            "Content-Type": "application/json", // Ensure JSON content type
          },
        });
  
        // Step 3: Redirect to bKash payment page
        setPaymentUrl(paymentResponse.data.bkash_url);
      } else {
        setIsPaymentFailed(true);
        setIsSubmitting(false); // Stop submission if payment creation fails
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      setIsPaymentFailed(true);
      setIsSubmitting(false); // Stop submission if any error occurs
    }
  };

  // Handle payment callback
  useEffect(() => {
    const handlePaymentCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentID = urlParams.get("paymentID");
  
      if (paymentID && !isPaymentExecuted.current) {
        isPaymentExecuted.current = true; // Mark payment as executed
  
        try {
          // Step 1: Execute the payment
          const executeResponse = await axios.post(`${apiUrl}/execute_payment/`, {
            payment_id: paymentID,
          });
  
          if (executeResponse.data.status === "success") {
            setIsPaymentSuccess(true);
          } else {
            setIsPaymentFailed(true);
          }
        } catch (error) {
          console.error("Error executing payment:", error);
          setIsPaymentFailed(true);
        }
      }
    };
  
    handlePaymentCallback();
  }, [apiUrl]);

  // Redirect to bKash payment page if payment URL is set
  useEffect(() => {
    if (paymentUrl) {
      window.location.href = paymentUrl;
    }
  }, [paymentUrl]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, files, checked } = e.target;

    if (type === "checkbox") {
      setFormState((prev) => ({
        ...prev,
        [name]: prev[name]
          ? checked
            ? [...prev[name], value]
            : prev[name].filter((v) => v !== value)
          : checked
          ? [value]
          : [],
      }));
    } else if (type === "radio") {
      setFormState((prev) => ({ ...prev, [name]: value }));
    } else if (type === "file") {
      setFormState((prev) => ({ ...prev, [name]: files }));
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="bg-black lg:min-h-screen flex items-center justify-center px-2 py-5 lg:py-10">
      <div className="bg-white shadow-lg rounded-lg lg:max-w-4xl w-full p-8 border-t-8 border-[#e6b800]">
        <h2 className="text-3xl font-bold text-center text-black mb-6">
          Worker Registration
        </h2>
        <p className="text-center text-gray-700 mb-8">
          Fill out the form below to express your interest as a worker.
        </p>

        {isLoading ? (
          <p className="text-center text-gray-700">Loading form fields...</p>
        ) : (
          <form className="space-y-6" onSubmit={handleSubmit}>
            {formFields.map((field, index) => (
              <div key={index}>
                <label className="block text-black font-medium mb-2" htmlFor={field.name}>
                  {field.name} {field.required && <span className="text-red-500">*</span>}
                </label>

                {/* Render form fields based on their type */}
                {field.field_type === "text" && (
                  <input
                    type="text"
                    name={field.name}
                    id={field.name}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#e6b800]"
                    placeholder={`Enter your ${field.name}`}
                    required={field.required}
                    value={formState[field.name] || ""}
                    onChange={handleInputChange}
                  />
                )}
                {field.field_type === "email" && (
                  <input
                    type="email"
                    name={field.name}
                    id={field.name}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#e6b800]"
                    placeholder={`Enter your ${field.name}`}
                    required={field.required}
                    value={formState[field.name] || ""}
                    onChange={handleInputChange}
                  />
                )}
                {(field.field_type === "image" || field.field_type === "file") && (
                  <input
                    type="file"
                    name={field.name}
                    id={field.name}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#e6b800]"
                    required={field.required}
                    onChange={handleInputChange}
                  />
                )}
                {field.field_type === "checkbox" && (
                  <div className="flex flex-wrap gap-3">
                    {field.options.map((option, i) => (
                      <label key={i} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name={field.name}
                          value={option}
                          checked={formState[field.name]?.includes(option)}
                          onChange={handleInputChange}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <button
              type="submit"
              className="w-full bg-[#e1136e] text-white py-3 px-6 rounded-lg mt-4 hover:bg-pink-400 flex items-center justify-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spin /> : (
               <>
               <img src="https://freelogopng.com/images/all_img/1656235199bkash-logo-transparent.png" alt="bKash" className="h-6 mr-2" />
               <span className="mr-2">Pay Registration Fee</span>
               <span className="bg-white text-[#e1136e] font-semibold py-1 px-2 rounded-md text-sm">
                 {formMeta.payment_amount} BDT
               </span>
             </>
             
              )}
            </button>
          </form>
        )}
      </div>

      {/* Success Modal */}
      <Modal
        visible={isPaymentSuccess}
        onCancel={() => setIsPaymentSuccess(false)}
        footer={null}
        centered
      >
        <div className="text-center">
          <CheckCircleOutlined className="text-green-500 text-6xl mb-4" />
          <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
          <p>Your payment has been processed successfully.</p>
        </div>
      </Modal>

      {/* Failure Modal */}
      <Modal
        visible={isPaymentFailed}
        onCancel={() => setIsPaymentFailed(false)}
        footer={null}
        centered
      >
        <div className="text-center">
          <CloseCircleOutlined className="text-red-500 text-6xl mb-4" />
          <h2 className="text-2xl font-bold mb-2">Payment Failed!</h2>
          <p>There was an issue processing your payment. Please try again.</p>
        </div>
      </Modal>
    </div>
  );
};

export default WorkerApplication;